/**
 * Old-URL compatibility layer.
 *
 * Two historical URL shapes must 301 to their current home so existing inbound
 * links and indexed URLs never 404 and pass their SEO weight on:
 *
 *  1. GERMAN-AT-ROOT (pre-i18n): before the EN-first migration the site served
 *     German at the root (`/kurse`, `/wissen`, `/agb`, ...). Those are now the
 *     EN routes' German equivalents under `/de`, so every historical German root
 *     URL 301s to its `/de/...` home.
 *
 *  2. EN-SLUGGED FR/IT (pre-localization): when FR and IT first launched they
 *     reused the stable EN slugs under their locale prefix (`/fr/courses`,
 *     `/it/knowledge`, ...). Those URLs shipped in the sitemap. Now that FR/IT
 *     have native slugs (`/fr/cours`, `/it/sapere`, ...), each old EN-slugged
 *     URL 301s to its native equivalent in the same locale.
 *
 * Both mappings are DERIVED from the route map: add a route or a locale in
 * {@link ./routes.ts} and its legacy redirects appear automatically, always in
 * lockstep with the live URLs.
 */

import { DEFAULT_LOCALE, LOCALES, localePrefix } from './config'
import { ROUTES } from './routes'
import type { RouteKey } from './routes'

export type LegacyRedirect = {
  /** The old pattern (`$param` tokens preserved). */
  from: string
  /** The new pattern the old URL maps to. */
  to: string
}

function patternFrom(segments: readonly string[]): string {
  const tail = segments.join('/')
  return tail ? `/${tail}` : '/'
}

/**
 * Build the legacy redirect table.
 *
 * German-at-root: a route contributes when its German slug differs from its
 * English one - the bare German slug at the root used to be a real URL that now
 * lives under `/de`.
 *
 * EN-slugged FR/IT: for every non-default, non-German locale a route contributes
 * when its localized slug differs from the EN slug - the old `/{locale}/{en}`
 * URL now moves to `/{locale}/{native}`.
 */
export function buildLegacyRedirects(): LegacyRedirect[] {
  const redirects: LegacyRedirect[] = []
  const seen = new Set<string>()

  const push = (from: string, to: string) => {
    if (from === to) return
    if (seen.has(from)) return
    seen.add(from)
    redirects.push({ from, to })
  }

  for (const key of Object.keys(ROUTES) as RouteKey[]) {
    const { segments } = ROUTES[key]
    const enPattern = patternFrom(segments.en)

    // 1. German-at-root -> /de/... (only when the DE slug actually differs).
    const dePattern = patternFrom(segments.de)
    if (dePattern !== enPattern && dePattern !== '/') {
      push(dePattern, `${localePrefix('de')}${dePattern}`)
    }

    // 2. EN-slugged FR/IT (and any future prefixed locale) -> native slug.
    for (const locale of LOCALES) {
      if (locale === DEFAULT_LOCALE || locale === 'de') continue
      const nativePattern = patternFrom(segments[locale])
      if (nativePattern === enPattern) continue // slug unchanged, no move
      if (enPattern === '/') continue // the locale home never moves
      const prefix = localePrefix(locale)
      push(`${prefix}${enPattern}`, `${prefix}${nativePattern}`)
    }
  }

  return redirects
}

/**
 * The redirect table is derived purely from the static route map, so it never
 * changes within a process. Build it once, lazily, on the first lookup and
 * reuse it for every subsequent request instead of rebuilding it per call.
 */
let cachedRedirects: LegacyRedirect[] | null = null

function getLegacyRedirects(): LegacyRedirect[] {
  if (cachedRedirects === null) cachedRedirects = buildLegacyRedirects()
  return cachedRedirects
}

/**
 * Resolve a request pathname to a legacy redirect target, or `null` when the
 * path is not a recognised legacy URL.
 *
 * Static legacy slugs match exactly; dynamic legacy slugs (e.g. `/kurse/$slug`
 * or `/fr/courses/$slug`) match by segment shape and carry their captured params
 * into the target.
 */
export function resolveLegacyRedirect(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/u, '') || '/'
  const requestSegments = clean.split('/').filter(Boolean)

  for (const redirect of getLegacyRedirects()) {
    const fromSegments = redirect.from.split('/').filter(Boolean)
    if (fromSegments.length !== requestSegments.length) continue

    const params: string[] = []
    let matched = true
    for (let i = 0; i < fromSegments.length; i += 1) {
      const tpl = fromSegments[i]
      const actual = requestSegments[i]
      if (tpl.startsWith('$')) {
        params.push(actual)
      } else if (tpl !== actual) {
        matched = false
        break
      }
    }
    if (!matched) continue

    // Substitute captured params into the target pattern in order.
    let paramIndex = 0
    const target = redirect.to
      .split('/')
      .map((segment) =>
        segment.startsWith('$') ? params[paramIndex++] : segment,
      )
      .join('/')
    return target
  }

  return null
}
